import { useEffect, useState } from 'react';
import { Check, Plus, X } from 'lucide-react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  ICS204_DELETE_BTN,
  ICS204_HINT,
  ICS204_ICON_BTN,
  ICS204_MUTED,
  ICS204_OUTLINE_BTN,
} from './assignmentCardStyles';
import {
  emptyWorkAssignmentDraft,
  type AssignmentCardFormData,
  type WorkAssignmentDraft,
} from './assignmentCardFormTypes';
import { Ics204RichTextField } from './Ics204Fields';
import { ics204HtmlToPlainText, isIcs204RichTextEmpty } from './ics204RichText';

const cloneDraft = (row: WorkAssignmentDraft): WorkAssignmentDraft => ({
  ...row,
  resourceNeeds: row.resourceNeeds.map((need) => ({ ...need })),
});

const draftsEqual = (a: WorkAssignmentDraft, b: WorkAssignmentDraft): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

type WorkAssignmentRowProps = {
  rowIndex: number;
  onConfirm: () => void;
  onCancel: () => void;
};

const WorkAssignmentRow = ({
  rowIndex,
  onConfirm,
  onCancel,
}: WorkAssignmentRowProps) => {
  const base = `workAssignments.${rowIndex}`;

  return (
    <div
      className="tw:mb-2 tw:flex tw:items-start tw:gap-2 tw:border-b tw:border-[var(--border-primary)] tw:pb-2 tw:last:mb-0 tw:last:border-b-0 tw:last:pb-0"
      data-testid="ics204-edit-work-assignment-row"
    >
      <Ics204RichTextField
        fieldName={`${base}.name`}
        testId="ics204-edit-work-assignment-name"
        className="tw:flex-1"
        minHeight="80px"
        rules={{
          validate: (value: string) => {
            if (isIcs204RichTextEmpty(value)) {
              return 'Work assignment name is required';
            }
            if (ics204HtmlToPlainText(value).length > 255) {
              return 'This maximum length is 255 characters';
            }
            return true;
          },
        }}
      />
      <div className="tw:ml-auto tw:flex tw:shrink-0 tw:items-center tw:gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Confirm work assignment"
          className={ICS204_ICON_BTN}
          data-testid="ics204-edit-confirm-work-assignment"
          onClick={onConfirm}
        >
          <Check className="tw:h-4 tw:w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Cancel work assignment"
          className={ICS204_DELETE_BTN}
          data-testid="ics204-edit-delete-work-assignment"
          onClick={onCancel}
        >
          <X className="tw:h-4 tw:w-4" />
        </Button>
      </div>
    </div>
  );
};

/**
 * Box 6. Work assignments are their own entity shared with the ICS-215 and Work
 * Analysis views, so edits here are visible there too. Only the name is edited
 * on this card; resource needs and the other work-assignment fields live on
 * those views. Draft `resourceNeeds` stay in the form so saves do not wipe
 * ICS-215 data.
 */
const AssignmentCardWorkAssignmentsEdit = () => {
  const { control, setValue, getValues, setError, clearErrors } =
    useFormContext<AssignmentCardFormData>();
  const { fields, prepend, remove } = useFieldArray({
    control,
    name: 'workAssignments',
  });
  // `fields` is a snapshot, so archive flags are watched to hide rows live.
  const rows = useWatch({ control, name: 'workAssignments' });

  /** RHF field-array key → last confirmed draft. */
  const [snapshots, setSnapshots] = useState<
    Record<string, WorkAssignmentDraft>
  >({});
  const [confirmedKeys, setConfirmedKeys] = useState<Set<string>>(
    () => new Set()
  );

  // Seed confirmed snapshots for rows that already exist on the server.
  useEffect(() => {
    setSnapshots((prev) => {
      let next = prev;
      fields.forEach((field, index) => {
        const row = rows?.[index];
        if (!row || row.archived || !row.id || next[field.id]) return;
        if (next === prev) next = { ...prev };
        next[field.id] = cloneDraft(row);
      });
      return next;
    });
    setConfirmedKeys((prev) => {
      let changed = false;
      const next = new Set(prev);
      fields.forEach((field, index) => {
        const row = rows?.[index];
        if (!row || row.archived || !row.id || next.has(field.id)) return;
        next.add(field.id);
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [fields, rows]);

  const archiveOrRemove = (index: number, id: string) => {
    if (id) {
      // Saved rows archive on submit; there is no DELETE endpoint.
      setValue(`workAssignments.${index}.archived`, true, {
        shouldDirty: true,
      });
    } else {
      remove(index);
    }
  };

  const handleConfirm = (index: number, fieldKey: string) => {
    const row = getValues(`workAssignments.${index}`);
    if (isIcs204RichTextEmpty(row.name)) {
      setError(`workAssignments.${index}.name`, {
        type: 'required',
        message: 'Work assignment name is required',
      });
      return;
    }
    if (ics204HtmlToPlainText(row.name).length > 255) {
      setError(`workAssignments.${index}.name`, {
        type: 'maxLength',
        message: 'This maximum length is 255 characters',
      });
      return;
    }
    clearErrors(`workAssignments.${index}.name`);
    setSnapshots((prev) => ({ ...prev, [fieldKey]: cloneDraft(row) }));
    setConfirmedKeys((prev) => new Set(prev).add(fieldKey));
  };

  const handleCancel = (index: number, fieldKey: string, id: string) => {
    const isConfirmed = confirmedKeys.has(fieldKey);
    const snapshot = snapshots[fieldKey];
    const current = getValues(`workAssignments.${index}`);

    if (!isConfirmed) {
      archiveOrRemove(index, id);
      return;
    }

    if (snapshot && !draftsEqual(snapshot, current)) {
      setValue(`workAssignments.${index}`, cloneDraft(snapshot), {
        shouldDirty: true,
      });
      clearErrors(`workAssignments.${index}.name`);
      return;
    }

    archiveOrRemove(index, id);
    setSnapshots((prev) => {
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
    setConfirmedKeys((prev) => {
      const next = new Set(prev);
      next.delete(fieldKey);
      return next;
    });
  };

  const visibleCount = fields.filter(
    (field, index) => !(rows?.[index]?.archived ?? field.archived)
  ).length;

  return (
    <div data-testid="ics204-edit-work-assignments">
      <p className={ICS204_HINT}>
        Work assignments are shared with the ICS-215. Changes here appear there
        as well.
      </p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={cn(ICS204_OUTLINE_BTN, 'tw:mt-2')}
        data-testid="ics204-edit-add-work-assignment"
        onClick={() => prepend(emptyWorkAssignmentDraft())}
      >
        <Plus className="tw:h-4 tw:w-4" />
        Add Work Assignment
      </Button>
      <div className="tw:mt-2">
        {visibleCount === 0 ? (
          <div className={ICS204_MUTED}>No work assignments</div>
        ) : (
          fields.map((field, index) => {
            const row = rows?.[index] ?? field;
            if (row.archived) return null;
            return (
              <WorkAssignmentRow
                key={field.id}
                rowIndex={index}
                onConfirm={() => handleConfirm(index, field.id)}
                onCancel={() => handleCancel(index, field.id, row.id)}
              />
            );
          })
        )}
      </div>
    </div>
  );
};

export default AssignmentCardWorkAssignmentsEdit;
